import {
  EnvironmentProviders,
  inject,
  Injectable,
  provideAppInitializer,
} from '@angular/core';
import _refiner from 'refiner-js';
import { environment } from '../../environments/environment';
import { type SupportedLanguage } from '../app.i18n';

/** User identity traits forwarded to Refiner. */
export interface RefinerIdentity {
  id: string;
  email: string;
  name: string | null;
  locale: SupportedLanguage;
}

/** Minimal wrapper around the `refiner-js` SDK. */
@Injectable({ providedIn: 'root' })
export class RefinerService {
  /** Tracks whether `setProject` has been attempted. */
  private isInitialized = false;

  /** Initializes the Refiner client once at app bootstrap. */
  async init(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      await _refiner('setProject', environment.refiner.environmentId);
      this.isInitialized = true;
    } catch (error) {
      console.error(error);
    }
  }

  /** Identifies the authenticated user with Refiner. */
  async identify(identity: RefinerIdentity, signature: string): Promise<void> {
    if (!this.isInitialized) {
      return;
    }

    const userPayload = {
      id: identity.id,
      email: identity.email,
      name: identity.name ?? undefined,
      locale: identity.locale,
    };

    try {
      await _refiner('identifyUser', userPayload, { signature });
    } catch (error) {
      console.error(error);
    }
  }

  /** Clears the Refiner identity on logout. */
  async reset(): Promise<void> {
    if (!this.isInitialized) {
      return;
    }

    try {
      await _refiner('resetUser');
      await _refiner('stopPinging');
    } catch (error) {
      console.error(error);
    }
  }
}

/** Best-effort app initializer for the Refiner SDK. */
export const provideRefinerInitializer = (): EnvironmentProviders =>
  provideAppInitializer(async () => {
    try {
      await inject(RefinerService).init();
    } catch {
      // Refiner should never block app bootstrap.
    }
  });
